import { useState } from 'react';
import { formatDate } from '@/features/common/formatDate';
import { indicatorLine } from '@/features/common/indicatorLine';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import type { ResearchAdoption } from '@/features/publicsite/types/research/ResearchAdoption';
import type { ResultAdoptionsProps } from '@/features/publicsite/types/research/ResultAdoptionsProps';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/publicsite/publicsite.css';

/**
 * The uptake recorded against one published result — ToR §6.7's "monitorizarea implementării
 * rezultatelor în practică", on the page where a reader meets the result itself.
 *
 * A `<details>` rather than a rendered list, and the reason is the fetch, not the layout: there
 * is no bulk endpoint for a project's adoptions, so a card that listed them eagerly would open
 * one request per result on a page that routinely shows a dozen. The count comes free with the
 * catalogue (one call for every card), and the rows are fetched the first time somebody opens
 * the block.
 *
 * Renders nothing at all when the count is zero. "0 aplicări" under every result would read as a
 * verdict on the research rather than as the absence of a report — most results have no adoption
 * recorded simply because no advisor has filed one yet.
 */
export function ResultAdoptions({ resultId, count }: ResultAdoptionsProps) {
  const { t, language } = useTranslation(publicCatalog);
  const [rows, setRows] = useState<ResearchAdoption[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Tracked separately from `rows`, because "already asked" and "have an answer" stopped being
  // the same thing once a failure could leave the second false forever. Guarding the fetch on
  // `rows === null` alone re-fired the request on every toggle after a failure.
  const [asked, setAsked] = useState(false);

  if (count <= 0) return null;

  // Fetched on the first open only — a stale count is not worth a second request, since the
  // listing and the counter are cached for the same minute by the same server.
  const load = () => {
    if (asked) return;
    setAsked(true);
    void publicCatalogApi
      .adoptions(resultId)
      .then(setRows)
      // A refused read has to end the "loading" line. Without this the block sat on it for good
      // and the rejection went unhandled — and because the counter says there ARE reports, an
      // empty list would be the one wrong thing to show: it would read as a contradiction.
      .catch(() => setFailed(true));
  };

  return (
    <details className="adoption-disclosure" onToggle={load}>
      <summary className="adoption-disclosure__summary">{t('adoptionCount', { count })}</summary>
      {failed ? (
        <p className="muted">{t('adoptionsFailed')}</p>
      ) : rows === null ? (
        <p className="muted">{t('loading')}</p>
      ) : (
        <ul className="adoption-disclosure__list">
          {rows.map((a) => {
            const indicator = indicatorLine(a.indicatorName, a.indicatorValue);
            return (
              <li key={a.id} className="adoption-disclosure__item">
                <p>
                  <strong>{a.adopterName}</strong>
                  {a.territorialUnit && ` · ${a.territorialUnit}`}
                </p>
                <p className="muted">
                  {formatDate(a.adoptedOn, language)}
                  {a.scale && ` · ${a.scale}`}
                </p>
                {indicator && <p>{indicator}</p>}
                {a.notes && <p className="muted">{a.notes}</p>}
                {/* Who observed it. An uptake claim with no named reporter is an anonymous
                    assertion about a third party's farm, which is not what §6.7 asks to
                    publish — the advisor's name is what makes it checkable. */}
                <p className="muted">{t('adoptionReportedBy', { name: a.reporter })}</p>
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
}
